import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import Select from './Select/Select';
import Textarea from './Textarea/Textarea';
import styles from './SkillLevelDescriptions.module.scss';

function SkillLevelDescriptions({ skills, levels, reloadAll }) {
  const [descriptions, setDescriptions] = useState([]);
  const [selectedSkillId, setSelectedSkillId] = useState(skills[0]?.id || '');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [filteredSkills, setFilteredSkills] = useState(skills);
  const [filteredLevels, setFilteredLevels] = useState(levels);
  const [editing, setEditing] = useState({}); // { [levelId]: description }
  const [saving, setSaving] = useState({}); // { [levelId]: true/false }
  const timers = useRef({});

  // Load departments on mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await api.getDepartments();
        setDepartments(res.data);
        if (res.data.length > 0) {
          setSelectedDepartmentId(res.data[0].id);
        }
      } catch (error) {
        console.error('Error loading departments:', error);
      }
    };
    
    loadDepartments();
  }, []);

  // Filter skills and levels when department changes
  useEffect(() => {
    if (selectedDepartmentId) {
      const filteredSkills = skills.filter(skill => skill.department_id === parseInt(selectedDepartmentId));
      const filteredLevels = levels.filter(level => level.department_id === parseInt(selectedDepartmentId));
      
      setFilteredSkills(filteredSkills);
      setFilteredLevels(filteredLevels);
      
      // Reset selected skill if it's not in the filtered list
      if (filteredSkills.length > 0 && !filteredSkills.some(skill => skill.id === parseInt(selectedSkillId))) {
        setSelectedSkillId(filteredSkills[0].id);
      }
    } else {
      setFilteredSkills(skills);
      setFilteredLevels(levels);
    }
  }, [selectedDepartmentId, skills, levels, selectedSkillId]);

  useEffect(() => {
    loadDescriptions();
  }, [selectedDepartmentId]);

  useEffect(() => {
    // Reset editing state when skill changes
    setEditing({});
    setSaving({});
  }, [selectedSkillId]);

  const loadDescriptions = async () => {
    try {
      const params = selectedDepartmentId ? { department_id: selectedDepartmentId } : {};
      const res = await api.getSkillLevelDescriptions(params);
      setDescriptions(res.data);
    } catch (e) {
      setDescriptions([]);
    }
  };

  const getDescription = (levelId) => {
    return descriptions.find(d => d.skill_id === Number(selectedSkillId) && d.level_id === levelId);
  };

  const handleEdit = (levelId, value) => {
    setEditing(editing => ({ ...editing, [levelId]: value }));
    setSaving(saving => ({ ...saving, [levelId]: true }));
    if (timers.current[levelId]) clearTimeout(timers.current[levelId]);
    timers.current[levelId] = setTimeout(() => saveDescription(levelId, value), 2000);
  };

  const saveDescription = async (levelId, value) => {
    const existing = getDescription(levelId);
    try {
      if (existing) {
        await api.updateSkillLevelDescription(existing.id, { 
          description: value,
          department_id: selectedDepartmentId
        });
      } else {
        await api.createSkillLevelDescription({ 
          skill_id: selectedSkillId, 
          level_id: levelId, 
          description: value,
          department_id: selectedDepartmentId
        });
      }
      await loadDescriptions();
      if (reloadAll) reloadAll();
    } catch (e) {
      // Optionally show error
    } finally {
      setSaving(saving => ({ ...saving, [levelId]: false }));
    }
  };

  // Transform skills for Select component
  const skillOptions = filteredSkills.map(skill => ({
    value: skill.id,
    label: skill.title
  }));

  // Transform departments for Select component
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  return (
    <div className={styles.container}>
      <div className={styles.selectContainer}>
      <h3>Skill Level Descriptions</h3>
        <Select
          options={departmentOptions}
          showSearch={false}
          value={selectedDepartmentId}
          onChange={(value) => setSelectedDepartmentId(value)}
          label="Department"
          id="department-select"
          className={styles.select}
        />
        <Select
          options={skillOptions}
          value={selectedSkillId}
          onChange={(value) => setSelectedSkillId(value)}
          label="Skill"
          id="skill-select"
          className={styles.select}
        />
      </div>
      <div className={styles.descriptionsContainer}>
        {filteredLevels.map(level => {
          const existing = getDescription(level.id);
          const value = editing[level.id] !== undefined ? editing[level.id] : (existing ? existing.description : '');
          return (
            <div key={level.id} className={styles.levelDescriptionItem}>
              <div className={styles.levelTitle}>{level.title}</div>
              <Textarea
                value={value}
                onChange={e => handleEdit(level.id, e.target.value)}
                rows={4}
                placeholder="No description"
                id={`level-desc-${level.id}`}
                className={styles.descriptionTextarea}
              />
              {saving[level.id] && <span className={styles.savingIndicator}>Saving…</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SkillLevelDescriptions; 